"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GwTransport = exports.GwTransportOptions = void 0;
const tslib_1 = require("tslib");
const events_typed_1 = require("@video/events-typed");
const state_flow_1 = require("@video/state-flow");
const zod_1 = require("zod");
const api_1 = require("../../../api");
const adapter_1 = require("../../../api/adapter");
const errors_1 = require("../../../internal/errors");
const bind_1 = require("../../../internal/utils/bind");
const timeout_helpers_1 = require("../../../internal/utils/timeout-helpers");
const state_flow_2 = require("../state-flow");
const Websocket = tslib_1.__importStar(require("../state-flow/websocket"));
function parseResponseId(event) {
    return Number(String(event).split("-", 2)[1]);
}
exports.GwTransportOptions = zod_1.z.object({
    clientPing: zod_1.z.boolean().default(true).describe("Whether to send a ping message to the server"),
    pingInterval: zod_1.z
        .number()
        .min(500)
        .max(60000)
        .default(5000)
        .describe("The interval in milliseconds to send a ping message to the server (0 - disabled)"),
    pingTimeout: zod_1.z
        .number()
        .min(500)
        .max(60000)
        .default(5000)
        .describe("The timeout in milliseconds to wait for a pong message from the server"),
    responseTimeout: zod_1.z
        .number()
        .min(500)
        .max(60000)
        .default(5000)
        .describe("The timeout in milliseconds to wait for a response from the server"),
    connectingTimeout: zod_1.z
        .number()
        .min(500)
        .max(60000)
        .default(5000)
        .describe("The timeout in milliseconds to wait for the connection to the server"),
    maxRetries: zod_1.z
        .number()
        .min(1)
        .max(Infinity)
        .default(Infinity)
        .describe("The maximum number of retries to connect to the server"),
    retryDelay: zod_1.z
        .array(zod_1.z.tuple([zod_1.z.number().min(0).max(60000), zod_1.z.number().min(0).max(600000)]))
        .default([
        [100, 600000],
        [15, 10000],
        [5, 1000],
        [0, 500],
    ])
        .describe("The delay in milliseconds to wait before retrying to connect to the server after a failed attempt"),
});
class GwTransport extends events_typed_1.EventEmitter {
    constructor(ctx) {
        super();
        this.ctx = ctx;
        this.websocket = null;
        (0, bind_1.makeBounded)(this);
        this.sf = state_flow_2.GwStateFlow.initialize(this);
        adapter_1.device.addEventListener("offline", this.handleOffline);
        adapter_1.device.addEventListener("online", this.handleOnline);
        adapter_1.device.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    get isConnected() {
        return Websocket.Connected.is(this.sf.websocket);
    }
    get isConnecting() {
        return Websocket.Connecting.is(this.sf.websocket);
    }
    initStateFlow(sf) {
        sf.handleExit(Websocket.Connected, this.onDisconnecting);
        sf.handleEnter(Websocket.Connecting, this.onConnecting);
        sf.handleEnter(Websocket.Connected, this.onConnected);
        sf.onCommit(Websocket.Closed, this.onClosed);
    }
    options(options) {
        this.sf.dispatch("options", exports.GwTransportOptions.parse(options)).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
    }
    async connect(uri, options) {
        return new Promise(async (resolve, reject) => {
            var _a;
            // try to connect and if it fails
            // the state would not change
            const result = await this.sf.dispatchAsync("connect", { uri });
            if (!result.ok) {
                this.sf.dispatch("error", { reason: (_a = result.message) !== null && _a !== void 0 ? _a : undefined }).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
                return;
            }
            this.once("connect", resolve);
            this.once("connectError", reject);
            if ((options === null || options === void 0 ? void 0 : options.jwt) != null) {
                await this.request("JWT", { token: options.jwt });
            }
        });
    }
    [Symbol.dispose]() {
        adapter_1.device.removeEventListener("offline", this.handleOffline);
        adapter_1.device.removeEventListener("online", this.handleOnline);
        adapter_1.device.removeEventListener("visibilitychange", this.handleVisibilityChange);
        if (this.websocket != null) {
            this.removeWebsocketListeners(this.websocket);
            this.websocket = null;
        }
    }
    finish() {
        this.sf.dispatch("finish", {}).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
    }
    send(event, params) {
        if ((0, state_flow_1.isTransitioning)(this.sf.websocket)) {
            throw (0, errors_1.createError)(api_1.ErrorCode.WebSocketInTransit, "The websocket state is in transit", {
                uri: this.sf.websocket.uri,
                event,
            });
        }
        this.sf
            .dispatch("messageSend", { event, data: params }, () => {
            if (this.websocket == null) {
                return state_flow_1.Result.reject("no websocket");
            }
            this.websocket.send(JSON.stringify({ event, data: params }));
            return state_flow_1.Result.ok();
        })
            .expect(state_flow_1.ResultKind.OK);
    }
    async request(type, data) {
        if ((0, state_flow_1.isTransitioning)(this.sf.websocket)) {
            throw (0, errors_1.createError)(api_1.ErrorCode.WebSocketInTransit, "The websocket state is in transit", {
                uri: this.sf.websocket.uri,
                event: "request",
            });
        }
        return new Promise((resolve, reject) => {
            try {
                this.sf
                    .dispatch("requestSend", { type, payload: data, resolve, reject }, (s) => {
                    if (this.websocket == null) {
                        return state_flow_1.Result.reject("no websocket");
                    }
                    this.websocket.send(JSON.stringify({
                        event: "request",
                        data: {
                            requestId: s.websocket.requestId,
                            type,
                            request: data,
                        },
                    }));
                    return state_flow_1.Result.ok();
                })
                    .expect(state_flow_1.ResultKind.OK);
            }
            catch (err) {
                reject(err);
            }
        });
    }
    onConnecting({ websocket }) {
        const ws = new WebSocket(websocket.uri);
        return state_flow_1.Result.transition(new Promise((resolve, reject) => {
            const ok = () => {
                ws.removeEventListener("open", ok);
                this.websocket = ws;
                this.addWebsocketListeners(ws);
                resolve(state_flow_1.Result.ok());
            };
            ws.addEventListener("open", ok);
            const fail = (ev) => {
                ws.removeEventListener("close", fail);
                resolve(state_flow_1.Result.reject(`websocket closed: ${ev.code}; attempt: ${this.sf.websocket.attempt}`));
            };
            ws.addEventListener("close", fail);
        }), websocket.options.connectingTimeout);
    }
    onConnected({ websocket }) {
        if (websocket.options.clientPing) {
            (0, timeout_helpers_1.setInterval)(this.ping, this.sf.websocket.options.pingInterval);
        }
        this.emit("connect");
        return state_flow_1.Result.ok();
    }
    onDisconnecting(states) {
        if (this.websocket == null) {
            return state_flow_1.Result.ignore("no websocket");
        }
        (0, timeout_helpers_1.removeInterval)(this.ping);
        this.removeWebsocketListeners(this.websocket);
        this.websocket.close(3000);
        return state_flow_1.Result.ok();
    }
    onClosed(states) {
        if (states.websocket.nextRetry > 0) {
            (0, timeout_helpers_1.setTimeout)(this.connect.bind(this, states.websocket.uri), states.websocket.nextRetry);
            return state_flow_1.Result.ok();
        }
        return state_flow_1.Result.ignore("no retry");
    }
    handleOffline() {
        this.sf.dispatch("networkOffline", {}).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
    }
    async handleOnline() {
        var _a;
        const result = await this.sf.dispatchAsync("networkOnline", {});
        if (!result.ok) {
            this.sf.dispatch("error", { reason: (_a = result.message) !== null && _a !== void 0 ? _a : undefined }).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
        }
    }
    // Why do we have this?
    handleVisibilityChange() { }
    addWebsocketListeners(ws) {
        ws.addEventListener("close", this.wsClose);
        ws.addEventListener("message", this.wsMessage);
        ws.addEventListener("error", this.wsError);
    }
    removeWebsocketListeners(ws) {
        ws.removeEventListener("close", this.wsClose);
        ws.removeEventListener("message", this.wsMessage);
        ws.removeEventListener("error", this.wsError);
    }
    wsClose() {
        this.sf.dispatch("internalClose", { reason: "ws close event" }).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
    }
    wsMessage(message) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if ((0, state_flow_1.isTransitioning)(this.sf)) {
            // delay the execution. this temporary workaround
            // will be removed after implementing a pool in the state-flow
            adapter_1.device.setTimeout(this.wsMessage.bind(this, message), 50);
            return;
        }
        if (message.data === "pong") {
            this.sf.dispatch("pong", {}).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
            return;
        }
        if (message.data === "ping" || message.data === "0") {
            (_a = this.websocket) === null || _a === void 0 ? void 0 : _a.send("pong");
            return;
        }
        let obj;
        try {
            obj = JSON.parse(message.data);
        }
        catch (err) {
            const msg = err instanceof Error ? err.stack : "unknown error";
            this.ctx.logger.error("websocket parse error", {
                data: message === null || message === void 0 ? void 0 : message.data,
                uri: this.sf.websocket.uri,
                err: msg,
            });
            return;
        }
        if (obj.event === "ready") {
            const resp = this.sf.dispatch("ready", {});
            if (!resp.ok && resp.kind !== state_flow_1.ResultKind.Ignored) {
                // nothing to do here
                return;
            }
        }
        if (obj.event === "ping") {
            (_b = this.websocket) === null || _b === void 0 ? void 0 : _b.send("pong");
            return;
        }
        if (obj.event === "request") {
            this.onServerRequest(obj);
            return;
        }
        if (obj.event === "callError") {
            let data;
            let messages = "unknown";
            let reason;
            try {
                data = JSON.parse(message === null || message === void 0 ? void 0 : message.data);
                reason = (_d = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.reason) !== null && _d !== void 0 ? _d : "unknown";
                if (((_f = (_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.causes) === null || _f === void 0 ? void 0 : _f.length) > 0) {
                    messages = ((_g = data.data.causes.map((v) => v.message)) !== null && _g !== void 0 ? _g : []).join("; ");
                }
                else {
                    messages = (_j = (_h = data === null || data === void 0 ? void 0 : data.data) === null || _h === void 0 ? void 0 : _h.errorMessage) !== null && _j !== void 0 ? _j : "unknown";
                }
            }
            catch (err) {
                // pass
            }
            this.ctx.logger.error(`Call Error: ${messages}`, {
                data,
                uri: this.sf.websocket.uri,
                event: obj.event,
            });
            if (!(0, state_flow_1.isTransitioning)(this.sf.websocket)) {
                this.sf.dispatch("error", { message: messages, reason }).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
            }
            this.emit("connectError", messages);
            return;
        }
        if (String(obj.event).startsWith("response-")) {
            this.onServerResponse(parseResponseId(obj.event), obj);
            return;
        }
        this.emit(`message:${obj.event}`, obj.data);
    }
    wsError(ev) {
        this.sf.dispatch("error", {}).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
    }
    ping() {
        const result = this.sf.dispatch("ping", {}, () => {
            if (this.websocket != null) {
                this.websocket.send("ping");
                return state_flow_1.Result.ok();
            }
            return state_flow_1.Result.ignore("no websocket");
        });
        if (!result.ok) {
            this.sf.dispatch("internalClose", { reason: "ping failed" }).expect(state_flow_1.ResultKind.OK, state_flow_1.ResultKind.Ignored);
        }
    }
    onServerResponse(id, response) {
        const request = this.sf.websocket.requestPool[id];
        if (request == null) {
            this.ctx.logger.error(`transport: Request with id ${id} not found`, { response });
            return;
        }
        this.sf
            .dispatch("responseReceive", { id, response }, () => {
            var _a;
            if (response.reasons != null && response.reasons.length > 0) {
                const reason = (_a = response.reasons[0]) === null || _a === void 0 ? void 0 : _a.text;
                const e = new Error(`request error: ${reason}`);
                request.reject(e);
                return state_flow_1.Result.ok();
            }
            request.resolve(response.data);
            return state_flow_1.Result.ok();
        })
            .expect(state_flow_1.ResultKind.OK);
    }
    onServerRequest(json) {
        const { method, requestId, request } = json.data;
        if (Number.isNaN(requestId)) {
            this.ctx.logger.error("transport: Invalid request from server", { json });
            return;
        }
        const response = (resp = {}, err = null) => {
            this.send("response", {
                requestId,
                response: resp,
                error: err,
            });
        };
        this.emit(`request:${method}`, request, response);
    }
}
exports.GwTransport = GwTransport;
//# sourceMappingURL=gw-transport.js.map